/**
 * Submeter Contrato → Associar Proposta: completed proposals + CCC identification.
 */
import { getClaimForPropostaId } from './proposalClaimLookup';
import {
  allServicosHaveTerminalScd,
  anyServicoConcluidoNotCancelled,
} from './proposalComercialBoard';

export type CompletedProposalForContract = {
  id: string;
  proposal: Record<string, unknown>;
  display: string;
  isCcc: boolean;
  cccSourceLabel: string | null;
  submittedAt: string | null;
};

type MarcacaoLikeForProposals = {
  id?: string | number;
  marcacao_id?: string;
  nome_empresa?: string;
  empresa?: string;
  form_data?: Record<string, unknown>;
};

const firstNonEmpty = (...values: (unknown)[]): string | null => {
  for (const v of values) {
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return null;
};

function proposalHasExplicitCccMarkers(proposal: Record<string, unknown>): boolean {
  if (proposal.submittedFromCcc === true) return true;
  const src = String(proposal.submittedBySource || proposal.submitted_by_source || '').toLowerCase();
  return src === 'ccc_recuperacao' || src === 'ccc_prospeccao' || src === 'ccc_retencao' || src === 'ccc';
}

export function normalizeCccProposalMarkers(
  proposal: Record<string, unknown>,
  marcacaoFormData?: Record<string, unknown>
): Record<string, unknown> {
  const fd = marcacaoFormData || {};
  const fromProspeccaoMarcacao =
    String(fd.cccSource || '').toLowerCase() === 'prospeccao' ||
    (fd.ccc_prospeccao_lead_id != null && String(fd.ccc_prospeccao_lead_id).trim());

  if (proposalHasExplicitCccMarkers(proposal) || fromProspeccaoMarcacao) {
    return {
      ...proposal,
      submittedFromCcc: true,
      submittedBySource:
        proposal.submittedBySource ||
        proposal.submitted_by_source ||
        (fromProspeccaoMarcacao ? 'ccc_prospeccao' : 'ccc'),
    };
  }
  return proposal;
}

export function isCccOriginatedProposal(
  proposal: Record<string, unknown>,
  marcacaoFormData?: Record<string, unknown>
): boolean {
  if (proposal.submittedFromCcc === true) return true;
  const src = String(proposal.submittedBySource || proposal.submitted_by_source || '').toLowerCase();
  if (src === 'ccc_recuperacao' || src === 'ccc_prospeccao' || src === 'ccc_retencao' || src === 'ccc') return true;
  const fd = marcacaoFormData || {};
  if (String(fd.cccSource || '').toLowerCase() === 'prospeccao') return true;
  if (String(fd.cccSource || '').toLowerCase() === 'retencao') return true;
  if (fd.ccc_prospeccao_lead_id != null && String(fd.ccc_prospeccao_lead_id).trim()) return true;
  if (fd.ccc_retencao_client_id != null && String(fd.ccc_retencao_client_id).trim()) return true;
  return false;
}

export function cccProposalSourceLabel(
  proposal: Record<string, unknown>,
  marcacaoFormData?: Record<string, unknown>
): string | null {
  const normalized = normalizeCccProposalMarkers(proposal, marcacaoFormData);
  if (!isCccOriginatedProposal(normalized, marcacaoFormData)) return null;
  const src = String(
    normalized.submittedBySource || normalized.submitted_by_source || ''
  ).toLowerCase();
  if (src === 'ccc_prospeccao') return 'CCC Prospeção';
  if (src === 'ccc_recuperacao') return 'CCC Recuperação';
  if (src === 'ccc_retencao') return 'CCC Reposição';
  if (String(marcacaoFormData?.cccSource || '').toLowerCase() === 'prospeccao') return 'CCC Prospeção';
  if (String(marcacaoFormData?.cccSource || '').toLowerCase() === 'retencao') return 'CCC Reposição';
  return 'CCC';
}

function getAllProposalsFromMarcacao(fd: Record<string, unknown> | undefined): Record<string, unknown>[] {
  if (!fd) return [];
  const raw = fd.proposalData;
  let list: Record<string, unknown>[] = [];
  if (Array.isArray(raw)) {
    list = raw.filter((p) => p && typeof p === 'object' && !Array.isArray(p)) as Record<string, unknown>[];
  } else if (raw && typeof raw === 'object') {
    list = [raw as Record<string, unknown>];
  }
  if (!list.length) return [];
  return [...list].sort((a, b) => {
    const ta = String(a.submittedAt || a.submitted_at || '');
    const tb = String(b.submittedAt || b.submitted_at || '');
    return tb.localeCompare(ta);
  });
}

function getPropostaIdsArray(fd: Record<string, unknown>): string[] {
  const raw = fd.propostaId ?? fd.proposta_id;
  if (Array.isArray(raw)) return raw.map(String);
  if (raw != null && String(raw).trim()) return [String(raw).trim()];
  return [];
}

function resolvePropostaId(
  proposal: Record<string, unknown>,
  fd: Record<string, unknown>,
  index: number
): string {
  const ids = getPropostaIdsArray(fd);
  const fromProposal = firstNonEmpty(proposal.propostaId, proposal.proposta_id);
  const fromList = ids[index] || ids[0];
  const raw = fromProposal || fromList || `pedido-${index + 1}`;
  return raw.startsWith('PRP-') ? raw : `PRP-${raw}`;
}

function resolveRequestBoardStatus(
  proposal: Record<string, unknown>,
  fd: Record<string, unknown>,
  propostaId: string
): { boardStatus: string; claim: Record<string, unknown> | null } {
  const claimedRaw = fd.claimedProposals;
  const claimedMap =
    typeof claimedRaw === 'object' && claimedRaw !== null && !Array.isArray(claimedRaw)
      ? (claimedRaw as Record<string, unknown>)
      : undefined;
  const claim = getClaimForPropostaId(claimedMap, propostaId);
  const servicos = Array.isArray(proposal.servicos) ? proposal.servicos : [];
  const scdRaw = claim?.service_completion_data;
  const scdList = Array.isArray(scdRaw) ? scdRaw : [];
  const claimStatus = String(claim?.status || '').toLowerCase();

  let boardStatus = 'available';
  if (claimStatus === 'cancelled') {
    boardStatus = 'cancelled';
  } else if (
    servicos.length > 0 &&
    allServicosHaveTerminalScd(servicos, scdList) &&
    !anyServicoConcluidoNotCancelled(servicos, scdList)
  ) {
    boardStatus = 'cancelled';
  } else if (
    claimStatus === 'completed' ||
    (servicos.length > 0 &&
      allServicosHaveTerminalScd(servicos, scdList) &&
      anyServicoConcluidoNotCancelled(servicos, scdList))
  ) {
    boardStatus = 'completed';
  } else if (
    servicos.length === 0 &&
    scdList.length > 0 &&
    claimStatus === 'completed'
  ) {
    boardStatus = 'completed';
  } else if (claim?.backoffice_user_id || claim?.claimed_by) {
    boardStatus = 'in_progress';
  }

  return { boardStatus, claim };
}

/** Resolve marcação from gestão store key (marcacao_id or numeric id). */
export function resolveMarcacaoInStore<T extends MarcacaoLikeForProposals>(
  marcacoesData: Record<string, T>,
  marcacaoKey: string | null | undefined
): T | null {
  if (!marcacaoKey) return null;
  const key = String(marcacaoKey).trim();
  if (marcacoesData[key]) return marcacoesData[key];
  return (
    Object.values(marcacoesData).find(
      (m) =>
        String(m.marcacao_id || '').trim() === key ||
        String(m.id || '').trim() === key
    ) || null
  );
}

export function buildCompletedProposalsForMarcacao(
  marcacao: MarcacaoLikeForProposals | undefined | null
): CompletedProposalForContract[] {
  if (!marcacao) return [];
  const formData = marcacao.form_data || {};
  const proposalsList = getAllProposalsFromMarcacao(formData).map((p) =>
    normalizeCccProposalMarkers(p, formData)
  );

  return proposalsList.flatMap((proposal, idx): CompletedProposalForContract[] => {
      const propostaId = resolvePropostaId(proposal, formData, idx);
      const propostaIdStr = String(propostaId);
      const { boardStatus, claim } = resolveRequestBoardStatus(proposal, formData, propostaIdStr);
      if (boardStatus !== 'completed') return [];

      const scdRaw = claim?.service_completion_data;
      const scdList = Array.isArray(scdRaw) ? scdRaw : [];
      const mergedServiceDetails = { ...(proposal?.serviceDetails || {}) } as Record<string, unknown>;
      scdList.forEach((scd: unknown) => {
        const row = (scd && typeof scd === 'object' ? scd : {}) as Record<string, unknown>;
        const instanceId = row.instanceId ?? row.instance_id;
        if (!instanceId) return;
        const emailData = (row.emailData || {}) as Record<string, unknown>;
        const existing = (mergedServiceDetails[String(instanceId)] || {}) as Record<string, unknown>;
        mergedServiceDetails[String(instanceId)] = {
          ...existing,
          idPropostaS2C: emailData.propostaId ?? emailData.idPropostaS2C ?? existing.idPropostaS2C,
          propostaId: emailData.propostaId ?? emailData.idPropostaS2C ?? existing.propostaId,
          valorProposta: emailData.investimento ?? existing.valorProposta,
          investimento: emailData.investimento ?? existing.investimento,
          idProposta: emailData.numeroProposta ?? emailData.idProposta ?? existing.idProposta,
          numeroProposta: emailData.numeroProposta ?? existing.numeroProposta,
          iban: emailData.iban ?? existing.iban,
          validade: emailData.validade ?? emailData.dataValidade ?? existing.validade,
          ...emailData,
        };
      });

      const mergedProposal: Record<string, unknown> = {
        ...proposal,
        serviceDetails: mergedServiceDetails,
      };
      const empresa = (mergedProposal.empresa || {}) as Record<string, unknown>;
      const companyName =
        empresa.nomeEmpresa || marcacao.nome_empresa || marcacao.empresa || 'Sem nome';
      const nif = empresa.nifEmpresa || '';
      const cccLabel = cccProposalSourceLabel(mergedProposal, formData);
      const display = nif
        ? `${propostaIdStr} – ${companyName} (NIF: ${nif})`
        : `${propostaIdStr} – ${companyName}`;

      return [
        {
          id: propostaIdStr,
          proposal: mergedProposal,
          display,
          isCcc: cccLabel != null,
          cccSourceLabel: cccLabel,
          submittedAt: firstNonEmpty(proposal.submittedAt, proposal.submitted_at),
        },
      ];
    });
}

export type SignatureAssociateOption = {
  associateId: string;
  display: string;
  isRefazer: boolean;
  cccSourceLabel: string | null;
  searchText: string;
};

export function isProposalRefazer(proposal: Record<string, unknown>): boolean {
  if (proposal.isRefazer === true) return true;
  return ['true', '1', 'yes'].includes(String(proposal.isRefazer ?? '').toLowerCase());
}

function marcacaoHasProposalData(marcacao: MarcacaoLikeForProposals): boolean {
  const fd = marcacao.form_data || {};
  const pd = fd.proposalData;
  if (Array.isArray(pd)) return pd.length > 0;
  if (pd && typeof pd === 'object' && Object.keys(pd as object).length > 0) return true;
  const propostaIds = fd.propostaId ?? fd.proposta_id;
  if (Array.isArray(propostaIds) && propostaIds.length > 0) return true;
  if (propostaIds != null && String(propostaIds).trim()) return true;
  return false;
}

function nifFromMarcacaoLike(marcacao: MarcacaoLikeForProposals): string {
  const formData = marcacao.form_data || {};
  const empresa =
    formData.empresa && typeof formData.empresa === 'object'
      ? (formData.empresa as Record<string, unknown>)
      : {};
  const proposalData = formData.proposalData;
  let proposalEmpresa: Record<string, unknown> = {};
  if (Array.isArray(proposalData) && proposalData.length > 0 && proposalData[0]?.empresa) {
    proposalEmpresa = proposalData[0].empresa as Record<string, unknown>;
  } else if (
    proposalData &&
    typeof proposalData === 'object' &&
    !Array.isArray(proposalData) &&
    (proposalData as Record<string, unknown>).empresa
  ) {
    proposalEmpresa = (proposalData as Record<string, unknown>).empresa as Record<string, unknown>;
  }
  return String(
    formData.nifEmpresa || empresa.nifEmpresa || proposalEmpresa.nifEmpresa || ''
  ).trim();
}

function companyNameFromMarcacaoLike(marcacao: MarcacaoLikeForProposals): string {
  return String(marcacao.nome_empresa || marcacao.empresa || 'Sem nome').trim();
}

function marcacaoCccLabelFromForm(formData: Record<string, unknown>, marcacaoId: string): string | null {
  if (String(formData.cccSource || '').toLowerCase() === 'prospeccao' || marcacaoId.startsWith('PROSP-')) {
    return 'CCC Prospeção';
  }
  if (formData.ccc_prospeccao_lead_id != null && String(formData.ccc_prospeccao_lead_id).trim()) {
    return 'CCC Prospeção';
  }
  const proposals = getAllProposalsFromMarcacao(formData).map((p) =>
    normalizeCccProposalMarkers(p, formData)
  );
  for (const proposal of proposals) {
    const label = cccProposalSourceLabel(proposal, formData);
    if (label) return label;
  }
  return null;
}

/** Build Pedir Assinatura Digital associate rows (marcação + Refazer proposal lines). */
export function buildSignatureAssociateOptions(
  marcacoes: MarcacaoLikeForProposals[]
): SignatureAssociateOption[] {
  const out: SignatureAssociateOption[] = [];
  const seenAssociateIds = new Set<string>();

  const pushOption = (option: SignatureAssociateOption) => {
    if (seenAssociateIds.has(option.associateId)) return;
    seenAssociateIds.add(option.associateId);
    out.push(option);
  };

  for (const marcacao of marcacoes) {
    if (!marcacaoHasProposalData(marcacao)) continue;

    const formData = marcacao.form_data || {};
    const marcacaoId = String(marcacao.marcacao_id || marcacao.id || '').trim();
    const dbIdRaw = (marcacao as { id_db?: string | number }).id_db ?? marcacao.id;
    const dbId = String(dbIdRaw ?? '').trim();
    if (!dbId) continue;

    const defaultCompany = companyNameFromMarcacaoLike(marcacao);
    const defaultNif = nifFromMarcacaoLike(marcacao);
    const marcacaoDisplay = defaultNif
      ? `${marcacaoId} – ${defaultCompany} (NIF: ${defaultNif})`
      : `${marcacaoId} – ${defaultCompany}`;

    const fd = marcacao.form_data || {};
    const marcacaoCccLabel = marcacaoCccLabelFromForm(fd, marcacaoId);

    pushOption({
      associateId: `marcacao-${dbId}`,
      display: marcacaoDisplay,
      isRefazer: false,
      cccSourceLabel: marcacaoCccLabel,
      searchText: `${marcacaoId} ${defaultCompany} ${defaultNif}`.toLowerCase(),
    });

    const proposalsList = getAllProposalsFromMarcacao(formData).map((p) =>
      normalizeCccProposalMarkers(p, formData)
    );

    proposalsList.forEach((proposal, idx) => {
      if (!isProposalRefazer(proposal)) return;

      const propostaId = resolvePropostaId(proposal, formData, idx);
      const empresa = (proposal.empresa || {}) as Record<string, unknown>;
      const companyName = String(empresa.nomeEmpresa || defaultCompany).trim();
      const nif = String(empresa.nifEmpresa || defaultNif || '').trim();
      const display = nif
        ? `${propostaId} – ${companyName} (NIF: ${nif})`
        : `${propostaId} – ${companyName}`;
      const cccLabel = cccProposalSourceLabel(proposal, formData) || marcacaoCccLabel;

      pushOption({
        associateId: `marcacao-${dbId}-${propostaId}`,
        display,
        isRefazer: true,
        cccSourceLabel: cccLabel,
        searchText: `${propostaId} ${companyName} ${nif} refazer ${marcacaoId}`.toLowerCase(),
      });
    });
  }

  return out;
}

export function signatureAssociateOptionMatchesSearch(
  option: SignatureAssociateOption,
  query: string
): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  return option.searchText.includes(q) || option.display.toLowerCase().includes(q);
}

export function findProposalInMarcacaoForm(
  formData: Record<string, unknown> | undefined,
  propostaId: string
): Record<string, unknown> | null {
  if (!formData || !propostaId.trim()) return null;
  const target = propostaId.trim();
  const proposals = getAllProposalsFromMarcacao(formData);
  for (let idx = 0; idx < proposals.length; idx += 1) {
    const resolved = resolvePropostaId(proposals[idx], formData, idx);
    if (resolved === target || resolved.replace(/^PRP-/, '') === target.replace(/^PRP-/, '')) {
      return proposals[idx];
    }
  }
  return null;
}
